import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ENUMS, SMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author @muhammedburakak
 */

const SMSForm = NiceModal.create(({ selectedOrders }) => {
	// Hooks and states declaration
	const { t } = useTranslation();
	const axios = useAxiosPrivate();
	const modal = useModal();

	const { showError, showSuccess } = useAlert();

	const [isLoading, setIsLoading] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					smsType: yup.string().nullable().required()
				})
				.required(),
		[]
	);
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			smsType: null
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const ids = Array.isArray(selectedOrders) ? selectedOrders.map((order) => order.ID) : [selectedOrders.ID];
	const titleIds = Array.isArray(selectedOrders)
		? selectedOrders.map((order) => order.orderID)
		: [selectedOrders.orderID];

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const response = await axios.post(SMS.SEND_SMS_ACTION, {
				orderIDs: ids,
				smsType: data.smsType
			});
			if (response.status === 200) {
				showSuccess();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			showError({
				message: error?.response?.data?.code || error?.response?.data?.message
			});
			AppLogger.error(error);
		}
		setIsLoading(false);
		modal.remove();
	};

	const title = `${t('smsForm')} (${titleIds.join(', ')})`;
	const titleTruncate = title.length > 50 ? `${title.substring(0, 50)}...)` : title;

	return (
		<Modal maxWidth="medium" fullwidth className="modal-smsForm">
			<ModalTitle>
				<Typography variant="p1">{titleTruncate}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<Alert content="youCanSendSmsByType" showIcon className="mb-3" />

					<FormItem label="smsType" required>
						<EnumSelect
							name="smsType"
							enumName={ENUMS.SEND_SMS_TYPES}
							placeholder="selectOne"
							value={watch('smsType')}
							setValue={(value) => {
								setValue('smsType', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						if (isDirty && !isSubmitSuccessful && !isSubmitting) {
							NiceModal.show(ConfirmationModal, {
								yesAction: () => {
									modal.remove();
								}
							});
						} else {
							modal.remove();
						}
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faPaperPlaneTop}
					label="send"
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default SMSForm;
